import { parseLookup, type LookupAnswer } from "odyssey-shared";
import type { LookupFetch } from "./lookup-client.ts";
import type { OnlineTimers } from "./online.ts";

/**
 * The page's lookup of who is not online (ADR-056's note of 2026-10-08 on one search box), owned by the page on the
 * desk's pattern. It runs from the left Search box's text as the GM types, beside the Online players list: plain text
 * is asked as a contains, `*text*`, as the online list matches it, and text with a `*` as typed; text with a space or
 * fewer than 3 characters besides the `*` asks nothing. It asks `LOOKUP_WAIT_MS` after the last key, starts at most
 * one lookup in `LOOKUP_GAP_MS`, keeps one request in flight and drops an answer a newer question overtook, so typing
 * spends at most 60 of the route's 60 a minute.
 */

/**
 * The wait after the last key before a lookup is asked.
 */
export const LOOKUP_WAIT_MS = 500;

/**
 * The least time between the starts of two lookups, as between two searches of the online list, so a GM typing
 * steadily starts at most one a second, inside the route's 60 a minute.
 */
export const LOOKUP_GAP_MS = 1000;

/**
 * Where a lookup stands: nothing asked; reading; answered; or over a limit, a session or a role that ended, or a
 * failure.
 */
export type LookupState = "idle" | "reading" | "ok" | "rate" | "session" | "forbidden" | "failed";

/**
 * What the list shows of the lookup: where it stands and the answer.
 */
export interface LookupSnapshot {
  readonly state: LookupState;
  readonly answer: LookupAnswer | null;
}

/**
 * What the model runs with: the fetch and the timers.
 */
export interface LookupModelOptions {
  readonly fetch: LookupFetch;
  readonly timers: OnlineTimers;
}

const IDLE: LookupSnapshot = Object.freeze({ state: "idle", answer: null });

/**
 * What the lookup is asked for the Search box's text.
 *
 * @param text The box's text.
 * @returns The pattern sent, or null when the text asks no lookup.
 * @example
 * lookupPattern("gre");
 * // "*gre*"
 */
export function lookupPattern(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed.includes(" ")) {
    return null;
  }
  const pattern = trimmed.includes("*") ? trimmed : `*${trimmed}*`;
  return parseLookup(pattern) === null ? null : pattern;
}

/**
 * The model.
 */
export class LookupModel {
  private readonly listeners = new Set<() => void>();
  private snapshot: LookupSnapshot = IDLE;
  private question = 0;
  private pattern: string | null = null;
  private due = false;
  private lastStart = Number.NEGATIVE_INFINITY;
  private flight: Promise<void> | null = null;
  private cancelWake: (() => void) | null = null;

  /**
   * @param options The fetch and the timers.
   */
  public constructor(private readonly options: LookupModelOptions) {}

  /**
   * Hears that the snapshot changed. Works unbound, as React calls it.
   *
   * @param listener Called on each change.
   * @returns Stops hearing.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the list shows now. Works unbound, as React calls it.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): LookupSnapshot => this.snapshot;

  /**
   * The Search box changed: its lookup is asked once the keys stop, or none is when the text asks none.
   *
   * @param text The box's text.
   */
  public readonly type = (text: string): void => {
    this.question += 1;
    this.sleep();
    this.pattern = lookupPattern(text);
    this.due = false;
    if (this.pattern === null) {
      this.set(IDLE);
      return;
    }
    this.cancelWake = this.options.timers.after(LOOKUP_WAIT_MS, () => {
      this.cancelWake = null;
      this.due = true;
      this.pump();
    });
  };

  /**
   * Forgets the lookup and one still waiting, as emptying the box does.
   */
  public readonly clear = (): void => {
    this.question += 1;
    this.sleep();
    this.pattern = null;
    this.due = false;
    this.set(IDLE);
  };

  /**
   * Settles once no request is in flight.
   *
   * @returns A promise that settles then.
   */
  public async idle(): Promise<void> {
    while (this.flight !== null) {
      await this.flight;
    }
  }

  /**
   * Starts the lookup that is due, unless one is in flight, which starts it as it ends, or the last started less than
   * `LOOKUP_GAP_MS` ago, which sets a wake-up for when it may.
   */
  private pump(): void {
    if (!this.due || this.flight !== null || this.pattern === null) {
      return;
    }
    const now = this.options.timers.now();
    const at = this.lastStart + LOOKUP_GAP_MS;
    if (now < at) {
      this.sleep();
      this.cancelWake = this.options.timers.after(at - now, () => {
        this.cancelWake = null;
        this.pump();
      });
      return;
    }
    this.lastStart = now;
    this.due = false;
    const question = this.question;
    this.set({ state: "reading", answer: this.snapshot.answer });
    this.flight = this.options
      .fetch(this.pattern)
      .then((reply) => {
        if (question !== this.question) {
          return;
        }
        this.set(reply.kind === "ok" ? { state: "ok", answer: reply.answer } : { state: reply.kind === "unreadable" ? "failed" : reply.kind, answer: null });
      })
      .finally(() => {
        this.flight = null;
        this.pump();
      });
  }

  /**
   * Cancels the wait for the keys to stop, if one is set.
   */
  private sleep(): void {
    this.cancelWake?.();
    this.cancelWake = null;
  }

  /**
   * Replaces the snapshot and tells the listeners.
   *
   * @param next The snapshot.
   */
  private set(next: LookupSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
